/**
 * Der öffentliche Tarif-Katalog (`/api/billing/catalog`) — einmal geladen,
 * von allen geteilt.
 *
 * Warum ein eigener Hook: Auf „Plan & Verbrauch" fragen die Sperrkarte für den
 * Zukauf und die Karte zur nächsten Stufe denselben Katalog ab. Zwei Bausteine,
 * die unabhängig mounten, holen ihn sonst zweimal (Regel #11: laufende
 * Abrufe teilen). Der Katalog ändert sich nicht, während jemand die Seite
 * offen hat — das aufgelöste Promise bleibt deshalb liegen; nur ein
 * gescheiterter Abruf wird beim nächsten Aufruf wiederholt.
 *
 * Und warum die Tarifstufe hier gerechnet wird: Welche Stufe „die nächste"
 * ist oder ein Merkmal trägt, stand bis 09/2026 als Name im Code (Basic, Pro,
 * Advisor — drei Stufen, die es im Katalog nie gab, zwei davon mit Preis). Der
 * Katalog ist die eine Wahrheit; der Code liest sie nur.
 */
import { useEffect, useState } from 'react'

import { billingAPI } from '../services/api'
import { guenstigsteStufeMit, type GateFaehigePlan } from '../shared/planGate'
import { tarifkarten, type Tarifkarte } from '../utils/katalogMarkt'
import { paketPreise } from '../utils/creditPreise'

export interface KatalogPreis {
  currency: string
  interval: string
  unit_amount_cents: number
}

export interface KatalogPlan {
  slug: string
  name: string
  /**
   * Einsprachig deutsch wie `name` — angezeigt wird sie über
   * `usePlanLabels().planDescription`. Der Katalog gab sie bis 18.09.2026 gar
   * nicht heraus; sie stand nur an `roles.description`, und nur
   * `/api/auth/pricing` lieferte sie.
   */
  description: string | null
  level: number
  /**
   * Woran der Tarif hängt — und damit, wo er zuweisbar ist. `user` nimmt die
   * Nutzerverwaltung an, `organization` die Account-Verwaltung, `none` ist der
   * Gratis-Stand (durch Abwesenheit erreicht, nicht durch Zuweisung).
   *
   * Ohne diese Angabe rät die Oberfläche: das Tarif-Dropdown bot bis
   * 13.09.2026 „Basic" und „Pro" an — zwei Slugs, die es im Katalog nie gab.
   */
  scope: 'user' | 'organization' | 'none'
  included_seats: number | null
  seat_billing: string | null
  prices: KatalogPreis[]
  /** Merkmalswerte je Code — `boolean` für Schalter, `number` für Kontingente. */
  features: Record<string, boolean | number | string>
}

export interface Katalog {
  plans: KatalogPlan[]
  features: Record<string, { name: string; description: string | null; category: string | null; value_type: string | null }>
  /**
   * Die Credit-Pakete aus dem Katalog (`usage_packs` + `prices`), kleinstes
   * zuerst — leer, wenn es keinen aktiven Eintrag gibt; dann wird der Zukauf
   * nicht angeboten. Seit 02.11.2026 die Leiter der Flotte (5.000 · 11.000 ·
   * 30.000 · 130.000 Credits), ohne Verfall.
   *
   * Die Preise stehen als LISTE da, in denselben sieben Währungen wie die
   * Tarife. Bis 18.09.2026 war es ein einzelner Betrag mit fester Währung
   * ("EUR"): Auf `/gb/english` stand damit ein Euro-Paket neben einem Plan in
   * Pfund.
   */
  sim_credit_packs: KatalogPaket[]
  /**
   * Wie viele Wertpapiere der kostenlose Tarif zur Wahl hat — die Papiere im
   * Ordner „System-Test-Daten“, gezählt beim Abruf. Die Preisseite nannte bis
   * 09.10.2026 fest „8“, der Ordner trug da schon sechzehn.
   */
  test_securities: number
  /**
   * Was ein Vorgang an Credits kostet (`credit_kosten`): `backtest`,
   * `monte_carlo`, `aktualisierung` (ein Tag). Die Preisseite und die
   * Start-Knöpfe nennen die Zahl, ohne sie selbst zu führen.
   */
  credit_kosten: Record<string, number>
  /** Was ein neues eigenes Konto einmal bekommt (`services/startguthaben`). */
  startguthaben: { credits: number; gueltig_tage: number }
  /**
   * Wie oft eine Aktualisierungsstufe im Monat läuft
   * (`watchlist_bericht.LAEUFE_JE_MONAT`) — dieselben Zahlen wie der erwartete
   * Verbrauch an Ordnern und Listen; der Rechner und die Beispiele rechnen damit.
   */
  aktualisierungen_je_monat: { taeglich: number; woechentlich: number; monatlich: number }
  /**
   * Closed Beta (14.10.2026): ob die App verkauft (`BILLING_SALES_ENABLED`).
   * `false` → jeder Kauf-Knopf führt zur Zwischenseite `/beta`
   * (`shared/closedBeta.ts`), die Kauf-Routen antworten 403 `sales_not_enabled`.
   */
  sales_open: boolean
}

export interface KatalogPaket {
  /** Schlüssel des Pakets (`sim_credit_pricing.PAKETE`). */
  key: string
  /** Credits je Paket — gekaufte Credits verfallen nicht. */
  size: number
  prices: { currency: string; unit_amount_cents: number }[]
}

/**
 * Die Spanne der Pakete in `waehrung`: kleinste und größte Menge und der Preis
 * des kleinsten Pakets („ab …") — in WÄHRUNGSEINHEITEN, nicht Cent.
 *
 * Gezählt werden nur Pakete mit einem Preis in dieser Währung; `null`, wenn es
 * keines gibt. Der Aufrufer bietet den Zukauf dann nicht an, statt einen
 * umgerechneten Betrag zu zeigen, der beim Kauf nicht gilt (Regel #7) —
 * dieselbe Regel wie in `tarifkarten`.
 */
export function paketSpanne(
  katalog: Katalog | undefined,
  waehrung: string,
): { min: number; max: number; ab: number } | null {
  // Die Preisliste liest nur `paketPreise` — eine Stelle für alle Paketpreise.
  const mitPreis = paketPreise(katalog, waehrung)
  if (!mitPreis.length) return null
  return { min: mitPreis[0].credits, max: mitPreis[mitPreis.length - 1].credits, ab: mitPreis[0].cents / 100 }
}

/**
 * Was ein Vorgang an Credits kostet, als Text für die Platzhalter `{backtest}`,
 * `{monte_carlo}` und `{aktualisierung}` — aus dem Katalog (`credit_kosten`),
 * nie als Zahl im Text. Fehlt ein Wert, steht „—“ statt einer erfundenen Zahl.
 */
export function creditKostenText(
  katalog: Katalog | undefined,
  locale: string,
): { backtest: string; monte_carlo: string; aktualisierung: string } {
  const kosten = (art: string) => {
    const wert = katalog?.credit_kosten?.[art]
    return typeof wert === 'number' ? wert.toLocaleString(locale) : '—'
  }
  return { backtest: kosten('backtest'), monte_carlo: kosten('monte_carlo'), aktualisierung: kosten('aktualisierung') }
}

/**
 * Was der Fundament-Baustein von einer Stufe braucht, plus der Katalogeintrag
 * selbst (für Slug und Merkmale).
 *
 * Der Katalog liefert Preise als Liste (Währungen × Intervalle), der Baustein
 * erwartet zwei Zahlen. Bewusst ohne Umrechnung: fehlt der EUR-Preis, gibt es
 * keinen — statt eines geratenen (Regel #7).
 */
export interface Stufe extends GateFaehigePlan {
  katalog: KatalogPlan
}

export function alsStufe(p: KatalogPlan): Stufe {
  const eur = (interval: string) =>
    p.prices.find((pr) => pr.currency === 'EUR' && pr.interval === interval)?.unit_amount_cents ?? 0
  return {
    name: p.name,
    price_monthly_cents: eur('monthly'),
    price_annual_cents: eur('yearly') || null,
    katalog: p,
  }
}

/**
 * Die günstigste kaufbare Stufe oberhalb der eigenen — oder `null`, wenn es
 * keine gibt (höchste Stufe) oder die eigene Rolle gar nicht im Katalog steht
 * (kundeneigene Corporate-Rollen). In beiden Fällen gibt es nichts zu bewerben.
 */
export function naechsteStufe(katalog: Katalog | undefined, eigenerSlug: string | null | undefined): Stufe | null {
  if (!katalog || !eigenerSlug) return null
  const eigene = katalog.plans.find((p) => p.slug === eigenerSlug)
  if (!eigene) return null
  const kandidaten = katalog.plans
    .filter((p) => p.level > eigene.level)
    .map(alsStufe)
    .filter((s) => s.price_monthly_cents > 0)
    .sort((a, b) => a.price_monthly_cents - b.price_monthly_cents)
  return kandidaten[0] ?? null
}

/**
 * Die günstigste kaufbare Stufe, deren Merkmale `traegt` erfüllen — z. B. die
 * erste, die 10-%-Schritte erlaubt. Für Beschriftungen wie „ab Einzel", die
 * sonst als Stufenname im Text stünden.
 */
export function guenstigsteStufe(
  katalog: Katalog | undefined,
  traegt: (merkmale: KatalogPlan['features']) => boolean,
): Stufe | null {
  if (!katalog) return null
  return guenstigsteStufeMit(katalog.plans.map(alsStufe), (s) => traegt(s.katalog.features))
}

/**
 * Zwei Preise DERSELBEN Stufe in DERSELBEN Währung — mehr braucht der
 * Jahresrabatt nicht. Beide Preisseiten haben das längst ausgerechnet
 * (`orderedRoles`): die Preisseite aus dem Katalog, die Registrierung aus
 * `/api/auth/pricing`. Die Währung wählt damit der Aufrufer, nicht dieser
 * Helfer — sonst stünde die Preis-Suche ein drittes Mal da.
 */
export interface Intervallpreise {
  price_monthly?: number | null
  price_yearly?: number | null
}

/**
 * Wie viel Prozent die Jahreszahlung spart — gerechnet, nicht getippt.
 *
 * Das Abzeichen am „Jährlich"-Umschalter stand als `−17%` im Markup, zweimal
 * (Preisseite und Registrierung). Auf Prod sind es am 14.09.2026 genau 1/6 =
 * 16,67 % (zehn Monatsbeiträge statt zwölf), in allen sieben Währungen und
 * allen drei bezahlten Stufen. Ändert jemand einen Jahrespreis im Katalog,
 * stimmt eine Zahl im Code still nicht mehr — deshalb Regel #23: kein Preis
 * und keine Tarifaussage im Quelltext.
 *
 * DER KLEINSTE RABATT GEWINNT. Das Abzeichen steht an EINEM Umschalter und
 * gilt damit für alle Stufen; der größte wäre für die billigste Stufe
 * eine Übertreibung. Eine bezahlte Stufe ganz OHNE Jahrespreis zählt als
 * null Prozent und nimmt das Abzeichen weg — denn für sie spart „Jährlich"
 * wirklich nichts (`renderPrice` zeigt dort weiter den Monatspreis).
 *
 * `null` heisst „kein Abzeichen" — nicht „0 %" (das wäre eine Werbung für
 * nichts). Gerundet wird auf ganze Prozent, weil das Abzeichen eine
 * Überschrift ist: Die genauen Beträge stehen in derselben Ansicht auf jeder
 * Karte („jährlich 390 € statt 468 €").
 */
export function jahresRabattProzent(stufen: Intervallpreise[]): number | null {
  const rabatte: number[] = []
  for (const stufe of stufen) {
    const monat = stufe.price_monthly
    // Die Gratis-Stufe hat keinen Jahrespreis und sagt über den Rabatt
    // nichts aus — sie darf ihn nicht auf null ziehen.
    if (!monat || monat <= 0) continue
    const jahr = stufe.price_yearly
    rabatte.push(jahr && jahr > 0 ? (1 - jahr / (monat * 12)) * 100 : 0)
  }
  if (rabatte.length === 0) return null
  const prozent = Math.round(Math.min(...rabatte))
  return prozent > 0 ? prozent : null
}

/** Tarifkarte und `tarifkarten` stehen rein in `utils/katalogMarkt.ts` (auch für den Server). */
export type { Tarifkarte }
export { tarifkarten }

/**
 * Was ein Vorgang kostet und welcher Weg am günstigsten ist — rein, in
 * `utils/creditPreise.ts` (mit `node --test` geprüft), hier weitergereicht wie
 * `tarifkarten`.
 */
export type { Intervall, PaketPreis, Weg } from '../utils/creditPreise'
export {
  guenstigsteWege,
  kleinstesPassendesPaket,
  lohntSichAb,
  monatsPreisCents,
  paketKosten,
  paketPreise,
  paketVorgangsPreis,
  vorgangsPreisCents,
} from '../utils/creditPreise'

let laufend: Promise<Katalog> | null = null

/**
 * Der Katalog, den eine Server-Seite (Next.js-Phase 2) schon geholt hat. Die
 * Preise sollen im Server-HTML stehen — für den Crawler, und damit React das
 * HTML im Browser übernimmt, startet der Browser mit demselben Katalog. Ohne
 * Vorlage starten `useKatalog` und die Preisseite leer wie bisher.
 */
let vorgelegt: Katalog | undefined

export function legeKatalogVor(katalog: Katalog): void {
  vorgelegt = katalog
}

export function vorgelegterKatalog(): Katalog | undefined {
  return vorgelegt
}

export function ladeKatalog(): Promise<Katalog> {
  if (!laufend) {
    laufend = billingAPI
      .catalog()
      .then((r) => r.data as Katalog)
      .catch((err: unknown) => {
        laufend = null
        throw err
      })
  }
  return laufend
}

export function useKatalog(): { katalog: Katalog | undefined; fehler: unknown } {
  const [katalog, setKatalog] = useState<Katalog | undefined>(vorgelegterKatalog)
  const [fehler, setFehler] = useState<unknown>(null)

  useEffect(() => {
    if (vorgelegt) return
    let abgebrochen = false
    ladeKatalog()
      .then((k) => {
        if (!abgebrochen) setKatalog(k)
      })
      .catch((err: unknown) => {
        // Der Katalog ist ein Nebenabruf: die Karte, die ihn braucht, bleibt
        // stehen — nur ohne Preis. Laut loggen, damit der Ausfall nicht als
        // „Karte ohne Preis" monatelang unbemerkt bleibt.
        console.error('Tarif-Katalog nicht ladbar', err)
        if (!abgebrochen) setFehler(err)
      })
    return () => {
      abgebrochen = true
    }
  }, [])

  return { katalog, fehler }
}
